import React from "react"
import { Box, ButtonBase, Stack, Typography, type StackProps } from "@mui/material"
import { AccentDot } from "../atoms/AccentDot"
import { ViraIcon } from "../atoms/ViraIcon"
import { ViraLink } from "../atoms/ViraLink"

export type BrandMarkProps = StackProps & {
  product?: "pro"
  showAccent?: boolean
}

export function BrandMark({
  product = "pro",
  showAccent = true,
  sx,
  ...rest
}: BrandMarkProps) {
  return (
    <Stack
      direction="row"
      alignItems="baseline"
      spacing={0.25}
      sx={sx}
      {...rest}
    >
      <Typography
        sx={{
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 700,
          fontSize: 25,
          color: "vira.ink",
          letterSpacing: "-0.25px",
        }}
      >
        vira
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 400,
          fontSize: 25,
          color: "vira.subtle",
          letterSpacing: "-0.25px",
        }}
      >
        {product}
      </Typography>
      {showAccent ? <AccentDot size={5} sx={{ ml: 0.5, mb: 2.5 }} /> : null}
    </Stack>
  )
}

export type TopMenuBarItem = {
  id: string
  label: string
  active?: boolean
  onClick?: () => void
  disabled?: boolean
}

export type TopMenuBarProps = {
  items: TopMenuBarItem[]
  trailing?: React.ReactNode
  practitionerName?: string
  onPractitionerClick?: () => void
}

/** App-bar actions row (nav links + optional practitioner menu). */
export function TopMenuBar({
  items,
  trailing,
  practitionerName,
  onPractitionerClick,
}: TopMenuBarProps) {
  return (
    <Stack
      direction="row"
      spacing={{ xs: 2, md: 4 }}
      alignItems="center"
      sx={{
        flexWrap: { xs: "wrap", md: "nowrap" },
        rowGap: 1,
        maxWidth: "100%",
        justifyContent: { xs: "flex-end", md: "flex-start" },
      }}
    >
      {items.map((item) => (
        <Stack key={item.id} sx={{ position: "relative", minWidth: 0 }}>
          <ViraLink
            variant="nav"
            active={item.active}
            disabled={item.disabled}
            onClick={item.onClick}
            sx={{
              whiteSpace: { xs: "normal", lg: "nowrap" },
              fontSize: { xs: 12.5, md: 13.5 },
              minHeight: 40,
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            {item.label}
          </ViraLink>
          {item.active ? (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: -8,
                height: 2,
                bgcolor: "vira.accent",
              }}
            />
          ) : null}
        </Stack>
      ))}
      {trailing}
      {practitionerName ? (
        onPractitionerClick ? (
          <ButtonBase
            onClick={onPractitionerClick}
            aria-haspopup="menu"
            aria-label={`Practitioner menu for ${practitionerName}`}
            sx={{
              display: "inline-flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 0.5,
              minHeight: 40,
              flexShrink: 0,
              borderRadius: 1,
              px: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 12.5, md: 13.5 },
                color: "vira.muted",
                maxWidth: { xs: 140, sm: "none" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {practitionerName}
            </Typography>
            <ViraIcon name="chevronDown" tone="muted" sx={{ fontSize: 14 }} />
          </ButtonBase>
        ) : (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              minHeight: 40,
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 12.5, md: 13.5 },
                color: "vira.muted",
                maxWidth: { xs: 140, sm: "none" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {practitionerName}
            </Typography>
            <ViraIcon name="chevronDown" tone="muted" sx={{ fontSize: 14 }} />
          </Stack>
        )
      ) : null}
    </Stack>
  )
}
